import React, { FC, SyntheticEvent, useEffect, useRef, useState } from 'react'

import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import * as S from './TimePicker.styles'
import {
  getInitialDisplayedTime,
  getTimeValue,
  getValidDisplayedTime,
  TIME_MASK,
} from './TimePicker.utils'

interface TimePickerProps {
  defaultValue?: string
  error?: string
  onChange: (value: string) => void
  hourPlaceholder?: string
  minutePlaceholder?: string
  value?: string
  className?: string
  isDisabled?: boolean
  initialValueHour?: string
  initialValueMinute?: string
}

export const TimePicker: FC<TimePickerProps> = ({
  defaultValue = '12:00',
  error,
  onChange,
  hourPlaceholder,
  minutePlaceholder,
  value,
  className,
  isDisabled,
  initialValueHour = '12',
  initialValueMinute = '00',
}) => {
  const [hourValue, setHourValue] = useState(initialValueHour)
  const [minuteValue, setMinuteValue] = useState(initialValueMinute)
  const [isPM, setIsPM] = useState(false)
  const [displayedTime, setDisplayedTime] = useState(value || defaultValue)

  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isDisabled) {
      const initTimeValues = getInitialDisplayedTime(value || defaultValue)
      const timeValues = initTimeValues.displayTime.split(':')

      setIsPM(initTimeValues.isPM)
      setDisplayedTime(initTimeValues.displayTime)
      setHourValue(timeValues[0] || initialValueHour)
      setMinuteValue(timeValues[1] || initialValueMinute)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, defaultValue, isDisabled])

  const handleOnHourChange = ({ currentTarget }: SyntheticEvent<HTMLInputElement>) => {
    const currentValue = currentTarget.value
    setHourValue(currentValue)
    setDisplayedTime(`${currentValue}:${minuteValue}`)
  }

  const handleOnHourBlur = () => {
    const isSingleDigitHour = hourValue.startsWith('0')
    if (isSingleDigitHour) {
      const currentHourValue = hourValue.replace('0', '')
      setHourValue(currentHourValue)
    }
  }

  const handleOnMinuteChange = ({ currentTarget }: SyntheticEvent<HTMLInputElement>) => {
    const currentValue = currentTarget.value
    setMinuteValue(currentValue)
    setDisplayedTime(`${hourValue}:${currentValue}`)
  }

  const handleOnPeriodChange = (isCurrentPeriodPM: boolean) => {
    setIsPM(isCurrentPeriodPM)

    const updatedTimeValue = getTimeValue({ isPM: isCurrentPeriodPM, time: displayedTime })

    if (updatedTimeValue) {
      onChange(updatedTimeValue)
    }
  }

  useOnClickOutside(ref, () => {
    if (isDisabled) return
    const validDisplayedTime = getValidDisplayedTime(displayedTime)
    const timeValues = validDisplayedTime.split(':')

    setDisplayedTime(validDisplayedTime)
    setHourValue(timeValues[0] || initialValueHour)
    setMinuteValue(timeValues[1] || initialValueMinute)

    const updatedTimeValue = getTimeValue({ isPM, time: displayedTime })

    if (updatedTimeValue && updatedTimeValue !== value) {
      onChange(updatedTimeValue)
    }
  })

  return (
    <S.TimePickerContainer ref={ref}>
      <S.MaskedTextField
        className={className}
        error={error}
        hideErrorIcon
        hideMaskGuide
        placeholder={hourPlaceholder}
        mask={TIME_MASK}
        onChange={handleOnHourChange}
        onFocus={handleOnHourBlur}
        value={hourValue}
        disabled={isDisabled}
      />
      <S.TimeDivider>:</S.TimeDivider>
      <S.MaskedTextField
        className={className}
        error={error}
        hideErrorIcon
        hideMaskGuide
        placeholder={minutePlaceholder}
        mask={TIME_MASK}
        onChange={handleOnMinuteChange}
        value={minuteValue}
        disabled={isDisabled}
      />
      <S.PeriodContainer>
        <S.TimePeriod
          isActive={!isPM}
          onClick={() => handleOnPeriodChange(false)}
          disabled={isDisabled}
          type="button"
        >
          AM
        </S.TimePeriod>
        <S.TimePeriod
          isActive={isPM}
          onClick={() => handleOnPeriodChange(true)}
          disabled={isDisabled}
          type="button"
        >
          PM
        </S.TimePeriod>
      </S.PeriodContainer>
    </S.TimePickerContainer>
  )
}
